import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UserGroupIcon,
  CalendarIcon,
  AcademicCapIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { Card, H1, H2, H3, Text, Badge, Spinner, Alert } from '../../components/ui';
import { formatDateLong } from '../../utils/date';

export const GuardianChildren: React.FC = () => {
  const { data: children, isLoading, error } = useQuery({
    queryKey: ['guardian-children'],
    queryFn: guardianApi.portal.getChildren,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load children. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <H1>My Children</H1>
        <Text className="mt-2 text-gray-600 dark:text-gray-400">
          View detailed information about your children
        </Text>
      </div>

      {children && children.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {children.map((child) => (
            <Link key={child.id} to={`/guardian/children/${child.id}`}>
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <H3 className="text-gray-900 dark:text-gray-100">
                      {child.first_name} {child.last_name}
                    </H3>
                    {child.relationship_type && (
                      <Badge variant="primary" size="sm" className="mt-2">
                        {child.relationship_type}
                      </Badge>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <CalendarIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                    <Text className="text-gray-600 dark:text-gray-400">
                      Age: {child.age} years
                    </Text>
                  </div>

                  <div className="flex items-center text-sm">
                    <AcademicCapIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                    <Text className="text-gray-600 dark:text-gray-400">
                      {child.grade?.name || 'Not assigned'}
                    </Text>
                  </div>

                  {child.classroom && (
                    <div className="flex items-center text-sm">
                      <UserGroupIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                      <Text className="text-gray-600 dark:text-gray-400">
                        Class: {child.classroom.name}
                      </Text>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      Date of Birth: {formatDateLong(child.date_of_birth)}
                    </Text>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end text-sm text-primary-600 dark:text-primary-400">
                  View Details
                  <ArrowRightIcon className="ml-1 w-4 h-4" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="p-8">
          <div className="text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <H2 className="mt-4 text-gray-900 dark:text-gray-100">No Children Found</H2>
            <Text className="mt-2 text-gray-500 dark:text-gray-400">
              You don't have any children linked to your account yet.
            </Text>
            <Text className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              If you received an invitation, please check your email and accept it to link your child to your account.
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
};